// The MOVE tool: grab a voxel and drag. If it's part of the current selection,
// the whole selection moves; otherwise the connected object (its attached
// voxels) is grabbed and moved. Owns the trigger-press start AND the
// move_selection commit — including for move drags the SELECT tool starts on a
// selected voxel (commit routes by interaction kind; see app/tool-registry).
//
// First tools/ slice (template for the rest): state comes from core/edit-core,
// the gesture handler from interaction/, and main injects only what it still
// owns (picks, mirror helpers, the clipboard slice, the grid's scene group).

import type * as THREE from "three";
import { uiState } from "../ui/state";
import { interaction } from "../interaction/state";
import { type PickTarget } from "../interaction/pick";
import { type MirrorConfig } from "../interaction/mirror";
import {
  finalizeMoveSelection,
  startMoveSelection,
} from "../interaction/handlers/move";
import { grid } from "../core/edit-core/grid";
import {
  selFloatValid,
  selection,
  setSelFloat,
} from "../core/edit-core/selection";
import { type ClipboardApi } from "../core/edit-core/clipboard";
import { type ToolRegistry } from "../app/tool-registry";

export interface MoveToolDeps {
  tools: ToolRegistry;
  pickFor: (i: number) => PickTarget | null; // the controller's last-frame pick
  gridGroup: THREE.Object3D;
  // The flood-filled connected component at a voxel (grabbing an unselected
  // voxel moves its whole attached object).
  connectedObject: (ix: number, iy: number, iz: number) => { ix: number; iy: number; iz: number }[];
  // Expand cells by their mirror reflections so a mirror grab moves both halves.
  mirrorExpandSelection: (
    cells: Iterable<{ ix: number; iy: number; iz: number }>,
  ) => { ix: number; iy: number; iz: number }[];
  mirrorConfigNow: () => MirrorConfig | null;
  screenDriven: () => boolean; // desktop mouse (true) vs VR laser (false)
  clipboard: ClipboardApi; // adoptSelFloat at grab time
}

export function installMoveTool(deps: MoveToolDeps): void {
  deps.tools.register({
    id: "move",
    claims: () => uiState.topMode === "move",
    start: (i) => {
      const pick = deps.pickFor(i);
      if (pick?.kind !== "voxel") return;
      // Is this grabbing an unmoved pending duplicate? Check before anything
      // mutates the selection or grid (startMoveSelection lifts cells).
      const grabsSelFloat =
        selFloatValid() && selection.has(pick.ix, pick.iy, pick.iz);
      if (!selection.has(pick.ix, pick.iy, pick.iz)) {
        // Mirror grabs both halves so they move symmetrically.
        selection.replace(
          deps.mirrorExpandSelection(deps.connectedObject(pick.ix, pick.iy, pick.iz)),
        );
      }
      const started = startMoveSelection(
        pick,
        i,
        grid,
        deps.gridGroup,
        selection,
        deps.screenDriven(),
        deps.mirrorConfigNow(),
      );
      if (started) deps.clipboard.adoptSelFloat(grabsSelFloat);
    },
    kinds: ["move_selection"],
    commit: (i) => {
      if (interaction.kind !== "move_selection" || interaction.controllerIndex !== i) {
        return;
      }
      // Capture what the selection now covers BEFORE finalize clears the
      // gesture, so the next move can restore it — selections stay
      // non-destructive until deselected. (finalizeMoveSelection only updates
      // the selection, not the displaced stash.)
      const covered = new Map(interaction.displaced);
      finalizeMoveSelection(selection);
      setSelFloat(covered);
    },
  });
}
